
import * as React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

interface User {
  full_name: string;
  rsvp_status?: 'accepted' | 'declined' | 'invited' | 'maybe';
  avatar_url?: string | null;
  guest_avatar_url?: string | null;
}

interface AvatarGroupProps {
  users: User[];
  maxVisible?: number;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const sizeClasses = {
  sm: "h-8 w-8 -ml-2.5",
  md: "h-10 w-10 -ml-3.5",
  lg: "h-12 w-12 -ml-4.5",
};

export const AvatarGroup = ({
  users,
  maxVisible = 4,
  size = "md",
  className,
}: AvatarGroupProps) => {
  const visibleUsers = users.slice(0, maxVisible);
  const remainingCount = Math.max(0, users.length - maxVisible);

  const getInitials = (fullName: string) => {
    return fullName
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarStyles = (user: User) => {
    if (user.rsvp_status === 'accepted') {
      return "!bg-black !text-white !border-2 !border-white";
    } else if (user.rsvp_status === 'declined') {
      return "!bg-gray-200 !text-black !border-2 !border-black";
    }
    return "!bg-white !text-black !border-2 !border-black";
  };

  const getFallbackStyles = (user: User) => {
    if (user.rsvp_status === 'accepted') {
      return "!bg-black !text-white";
    } else if (user.rsvp_status === 'declined') {
      return "!bg-gray-200 !text-black";
    }
    return "!bg-white !text-black";
  };

  return (
    <div className={cn("flex items-center", className)}>
      <div className="flex">
        {visibleUsers.map((user, index) => {
          // Determine which avatar URL to use, prioritizing guest_avatar_url
          const avatarUrl = user.guest_avatar_url || user.avatar_url;
          
          return (
            <Avatar
              key={index}
              className={cn(
                sizeClasses[size],
                getAvatarStyles(user),
                "first:ml-0 rounded-full overflow-hidden",
                index === 0 && "z-40",
                index === 1 && "z-30",
                index === 2 && "z-20",
                index === 3 && "z-10"
              )}
            >
              {avatarUrl ? (
                <AvatarImage 
                  src={avatarUrl} 
                  alt={user.full_name}
                />
              ) : null}
              <AvatarFallback className={cn("font-medium rounded-full", getFallbackStyles(user))}>
                {getInitials(user.full_name)}
              </AvatarFallback>
            </Avatar>
          );
        })}
        {remainingCount > 0 && (
          <div
            className={cn(
              sizeClasses[size],
              "!border-2 !border-black rounded-full !bg-white flex items-center justify-center -ml-3.5 z-0"
            )}
          >
            <span className="text-sm font-medium text-black">
              +{remainingCount}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
